import React, { useCallback, useState } from 'react'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  useToast,
  ConfirmDialog,
  Label,
  Switch,
} from '@biartbt/biartd'
import { Users, Shield, Building2, Menu, Plus, Pencil, Trash2 } from 'lucide-react'
import { CrudPage } from '@/components/Crud'
import type { Tenant, TenantStatus } from '../api/tenantsApi'
import { tenantsApi } from '../api/tenantsApi'
import type { RefDataItem } from '../api/refDataApi'
import { useRefDataGroups } from '../hooks/useRefDataGroups'
import { useLookups } from '../hooks/useLookups'
import { usersApi } from '@/modules/system/users/api/usersApi'
import type { UserItem } from '@/modules/system/users/api/usersApi'
import { organizationsApi } from '@/modules/system/organizations/api/organizationsApi'
import type { Organization } from '@/modules/system/organizations/api/organizationsApi'
import { menuAPI } from '@/services/menuAPI'
import type { MenuItem } from '@/services/menuAPI'
import { rolesApi } from '@/modules/system/roles/api/rolesApi'
import type { RoleItem } from '@/modules/system/roles/api/rolesApi'

/** Row actions: open modals (Pearl-style UX, no navigation) */
function TenantRowActions({
  tenant,
  onUsers,
  onRoles,
  onWorkplaces,
  onMenus,
}: {
  tenant: Tenant
  onUsers: (t: Tenant) => void
  onRoles: (t: Tenant) => void
  onWorkplaces: (t: Tenant) => void
  onMenus: (t: Tenant) => void
}) {
  const name = tenant.name ?? ''
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Button
        variant="soft"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          onUsers(tenant)
        }}
        title="Kullanıcılar"
        aria-label={`${name} - Kullanıcılar`}
        className="!py-1 !px-2 text-xs"
      >
        <Users className="w-3.5 h-3.5 shrink-0" aria-hidden />
        <span className="hidden sm:inline">Kullanıcılar</span>
      </Button>
      <Button
        variant="soft"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          onRoles(tenant)
        }}
        title="Roller"
        aria-label={`${name} - Roller`}
        className="!py-1 !px-2 text-xs"
      >
        <Shield className="w-3.5 h-3.5 shrink-0" aria-hidden />
        <span className="hidden sm:inline">Roller</span>
      </Button>
      <Button
        variant="soft"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          onWorkplaces(tenant)
        }}
        title="İşyerleri / Organizasyon Birimleri"
        aria-label={`${name} - İşyerleri`}
        className="!py-1 !px-2 text-xs"
      >
        <Building2 className="w-3.5 h-3.5 shrink-0" aria-hidden />
        <span className="hidden sm:inline">İşyerleri</span>
      </Button>
      <Button
        variant="soft"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          onMenus(tenant)
        }}
        title="Menüleri Oluştur"
        aria-label={`${name} - Menüler`}
        className="!py-1 !px-2 text-xs"
      >
        <Menu className="w-3.5 h-3.5 shrink-0" aria-hidden />
        <span className="hidden sm:inline">Menüleri Oluştur</span>
      </Button>
    </div>
  )
}

type TenantForm = {
  // Tenant (Auth.Tenant)
  name: string
  legalName: string
  industry: string
  taxNumber: string
  taxOffice: string
  organizationType: string
  foundationDate: string
  address: string
  country: string
  city: string
  authorizedPersonName: string
  phone: string
  email: string
  website: string
  description: string

  // Settings (Auth.TenantSettings)
  timezone: string
  language: string
  logoUrl: string
  logoFile: File | null

  // Admin user (CREATE only)
  adminFirstName: string
  adminLastName: string
  adminEmail: string
  adminPassword: string
  adminPasswordConfirm: string
}

function emptyToNull(v: string) {
  const t = v.trim()
  return t ? t : null
}

function formatMmDdYyyy(raw: string) {
  const digits = (raw ?? '').replace(/\D/g, '').slice(0, 8)
  const mm = digits.slice(0, 2)
  const dd = digits.slice(2, 4)
  const yyyy = digits.slice(4, 8)
  if (digits.length <= 2) return mm
  if (digits.length <= 4) return `${mm}/${dd}`
  return `${mm}/${dd}/${yyyy}`
}

/** Converts UI "MM/DD/YYYY" or empty to API: null or ISO date "yyyy-MM-dd" for DateTime? */
function foundationDateToApi(value: string): string | null {
  const t = (value ?? '').trim()
  if (!t) return null
  const digits = t.replace(/\D/g, '').slice(0, 8)
  if (digits.length !== 8) return null
  const mm = digits.slice(0, 2)
  const dd = digits.slice(2, 4)
  const yyyy = digits.slice(4, 8)
  const month = parseInt(mm, 10)
  const day = parseInt(dd, 10)
  const year = parseInt(yyyy, 10)
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return null
  return `${yyyy}-${mm}-${dd}`
}

const Section = ({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) => (
  <div className="rounded-[10px] border trezo-border-color bg-white dark:bg-[#0c1427] p-4 md:p-5">
    <div className="mb-4">
      <div className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</div>
      {description ? <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</div> : null}
    </div>
    <div className="grid gap-4">{children}</div>
  </div>
)

type RefDataSelectId = 'industry' | 'organizationType' | 'country' | 'city' | 'timezone' | 'language'
const REF_DATA_SELECT_IDS: RefDataSelectId[] = ['industry', 'organizationType', 'country', 'city', 'timezone', 'language']

function RefDataSelect(props: {
  id: RefDataSelectId
  formId: string
  search: Record<RefDataSelectId, string>
  setSearch: React.Dispatch<React.SetStateAction<Record<RefDataSelectId, string>>>
  refGroups: Record<string, { items: RefDataItem[]; loading: boolean; error: string | null }>
  touched: boolean
  errors: Record<string, string | undefined>
  isSubmitting: boolean
  label: string
  value: string
  onChange: (v: string) => void
  options: RefDataItem[]
  placeholder: string
}) {
  const {
    id,
    formId,
    search,
    setSearch,
    refGroups,
    touched,
    errors,
    isSubmitting,
    label,
    value,
    onChange,
    options,
    placeholder,
  } = props
  const triggerId = `${formId}-ref-${String(id)}`
  const q = (search[id] ?? '').trim().toLowerCase()
  const filtered = q
    ? options.filter((o) => (o.displayName ?? '').toLowerCase().includes(q) || (o.code ?? '').toLowerCase().includes(q))
    : options
  const status = refGroups[String(id)]
  const disabled = isSubmitting || !!status?.loading
  const showEmpty = !status?.loading && !status?.error && options.length === 0

  return (
    <FormField
      label={label}
      id={triggerId}
      error={touched && !!errors[String(id)]}
      errorMessage={touched ? errors[String(id)] : undefined}
      helperText={status?.error ? 'Veriler alınamadı' : undefined}
    >
      <Select value={value} onValueChange={onChange} disabled={disabled || showEmpty}>
        <SelectTrigger id={triggerId} aria-label={label}>
          <SelectValue
            placeholder={
              status?.loading
                ? 'Yükleniyor…'
                : status?.error
                  ? 'Veriler alınamadı'
                  : showEmpty
                    ? 'Veri yok'
                    : placeholder
            }
          />
        </SelectTrigger>
        <SelectContent>
          <div className="px-2 pt-2 pb-1">
            <input
              value={search[id]}
              onChange={(e) => setSearch((s) => ({ ...s, [id]: e.target.value }))}
              placeholder="Ara…"
              className="h-[55px] w-full rounded-md border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[17px] text-sm outline-none transition-all focus:border-primary-500"
              disabled={disabled}
            />
          </div>
          {status?.error ? (
            <div className="px-3 py-2 text-sm text-danger-500">Veriler alınamadı</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Sonuç yok</div>
          ) : (
            filtered.map((o) => (
              <SelectItem key={`${o.groupKey}:${o.code}`} value={o.code}>
                {o.displayName}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </FormField>
  )
}

export default function TenantsPage() {
  const refKeys = React.useMemo(() => ['industry'], [])
  const refDataGroups = useRefDataGroups(refKeys)
  const { toast } = useToast()

  // ---- Modals: Kullanıcılar, Roller, İşyerleri, Menüleri Oluştur (Pearl-style) ----
  const [showUsersModal, setShowUsersModal] = useState(false)
  const [selectedTenantForUsers, setSelectedTenantForUsers] = useState<Tenant | null>(null)
  const [tenantUsers, setTenantUsers] = useState<UserItem[]>([])
  const [usersLoading, setUsersLoading] = useState(false)

  const [showRolesModal, setShowRolesModal] = useState(false)
  const [selectedTenantForRoles, setSelectedTenantForRoles] = useState<Tenant | null>(null)
  const [tenantRoles, setTenantRoles] = useState<RoleItem[]>([])
  const [rolesLoading, setRolesLoading] = useState(false)

  const [showWorkplacesModal, setShowWorkplacesModal] = useState(false)
  const [selectedTenantForWorkplaces, setSelectedTenantForWorkplaces] = useState<Tenant | null>(null)
  const [tenantOrganizations, setTenantOrganizations] = useState<Organization[]>([])
  const [workplacesLoading, setWorkplacesLoading] = useState(false)

  const [showMenuCreateModal, setShowMenuCreateModal] = useState(false)
  const [menusToCreate, setMenusToCreate] = useState<MenuItem[]>([])
  const [targetTenantForMenus, setTargetTenantForMenus] = useState<Tenant | null>(null)
  const [menuCreateError, setMenuCreateError] = useState('')
  const [creatingMenus, setCreatingMenus] = useState(false)
  const [syncingAllMenus, setSyncingAllMenus] = useState(false)

  // Alt modallar: Yeni Kullanıcı, Yeni Rol, Yeni İşyeri
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [newUserData, setNewUserData] = useState({ email: '', firstName: '', lastName: '', phone: '', password: '', forcePasswordChange: true })
  const [addUserSubmitting, setAddUserSubmitting] = useState(false)

  const [showEditUserModal, setShowEditUserModal] = useState(false)
  const [editingUser, setEditingUser] = useState<UserItem | null>(null)
  const [editUserData, setEditUserData] = useState({ email: '', phone: '', isActive: true })
  const [editUserSubmitting, setEditUserSubmitting] = useState(false)

  const [showAddRoleModal, setShowAddRoleModal] = useState(false)
  const [newRoleData, setNewRoleData] = useState({ name: '', code: '', description: '' })
  const [addRoleSubmitting, setAddRoleSubmitting] = useState(false)

  const [showAddWorkplaceModal, setShowAddWorkplaceModal] = useState(false)
  const [newWorkplaceData, setNewWorkplaceData] = useState({ code: '', name: '', description: '', isActive: true })
  const [addWorkplaceSubmitting, setAddWorkplaceSubmitting] = useState(false)

  const [showEditWorkplaceModal, setShowEditWorkplaceModal] = useState(false)
  const [editingWorkplace, setEditingWorkplace] = useState<Organization | null>(null)
  const [editWorkplaceData, setEditWorkplaceData] = useState({ code: '', name: '', description: '', isActive: true })
  const [editWorkplaceSubmitting, setEditWorkplaceSubmitting] = useState(false)

  const [confirmDeleteWorkplaceOpen, setConfirmDeleteWorkplaceOpen] = useState(false)
  const [deleteWorkplaceTarget, setDeleteWorkplaceTarget] = useState<Organization | null>(null)
  const [deletingWorkplace, setDeletingWorkplace] = useState(false)

  const handleOpenUsers = useCallback((tenant: Tenant) => {
    setSelectedTenantForUsers(tenant)
    setShowUsersModal(true)
    setUsersLoading(true)
    usersApi.list({ tenantId: tenant.tenantId, pageSize: 500 }).then((res) => {
      setTenantUsers(res.items)
    }).catch(() => setTenantUsers([])).finally(() => setUsersLoading(false))
  }, [])

  const handleOpenRoles = useCallback((tenant: Tenant) => {
    setSelectedTenantForRoles(tenant)
    setShowRolesModal(true)
    setRolesLoading(true)
    rolesApi.list({ tenantId: tenant.tenantId, pageSize: 500 })
      .then((res) => setTenantRoles(res.items))
      .catch(() => setTenantRoles([]))
      .finally(() => setRolesLoading(false))
  }, [])

  const handleOpenWorkplaces = useCallback((tenant: Tenant) => {
    setSelectedTenantForWorkplaces(tenant)
    setShowWorkplacesModal(true)
    setWorkplacesLoading(true)
    organizationsApi.list({ tenantId: tenant.tenantId, pageSize: 500 }).then((res) => {
      setTenantOrganizations(res.items)
    }).catch(() => setTenantOrganizations([])).finally(() => setWorkplacesLoading(false))
  }, [])

  const handleOpenMenus = useCallback(async (tenant: Tenant) => {
    setMenuCreateError('')
    try {
      const res = await menuAPI.syncCatalog({ tenantId: tenant.tenantId, dryRun: true })
      const data = (res?.data ?? res) as Record<string, unknown>
      const inserted = (data?.inserted ?? data?.Inserted ?? 0) as number
      const updated = (data?.updated ?? data?.Updated ?? 0) as number
      const rawChanges = ((data?.changes ?? data?.Changes) ?? []) as Array<{ operation?: string; Operation?: string; label?: string; Label?: string; route?: string; Route?: string }>
      if (inserted === 0 && updated === 0) {
        toast({ title: 'Bilgi', description: 'Menü kataloğu güncel. Tüm menüler (root + alt menüler) zaten mevcut.', variant: 'info' })
        return
      }
      const changes = rawChanges.filter((c) => (c.operation ?? c.Operation) === 'Insert' || (c.operation ?? c.Operation) === 'Update')
      setMenusToCreate(changes.map((c) => ({ id: 0, label: (c.label ?? c.Label) ?? '', route: (c.route ?? c.Route) ?? '', children: [] as MenuItem[], sortOrder: 0, isActive: true, parentId: undefined })))
      setTargetTenantForMenus(tenant)
      setShowMenuCreateModal(true)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Menüler alınamadı'
      setMenuCreateError(msg)
      toast({ title: 'Hata', description: msg, variant: 'danger' })
    }
  }, [toast])

  const handleCreateMenus = useCallback(async () => {
    if (!targetTenantForMenus) return
    setCreatingMenus(true)
    setMenuCreateError('')
    try {
      const res = await menuAPI.syncCatalog({
        tenantId: targetTenantForMenus.tenantId,
        dryRun: false,
        disableNotInCatalog: true,
      })
      const data = (res?.data ?? res) as Record<string, unknown>
      const inserted = (data?.inserted ?? data?.Inserted ?? 0) as number
      const updated = (data?.updated ?? data?.Updated ?? 0) as number
      const parts: string[] = []
      if (inserted > 0) parts.push(`${inserted} menü oluşturuldu (root + alt menüler)`)
      if (updated > 0) parts.push(`${updated} menü güncellendi`)
      if (parts.length) {
        toast({ title: 'Menüler', description: parts.join('. '), variant: 'success' })
      }
      setShowMenuCreateModal(false)
    } catch (err: unknown) {
      setMenuCreateError(err instanceof Error ? err.message : 'Oluşturulamadı')
    } finally {
      setCreatingMenus(false)
    }
  }, [targetTenantForMenus, toast])

  const handleSyncAllTenantsMenus = useCallback(async () => {
    setSyncingAllMenus(true)
    setMenuCreateError('')
    try {
      const res = await tenantsApi.list({ pageSize: 500 })
      const tenants = res.items
      let totalInserted = 0
      let totalUpdated = 0
      for (const t of tenants) {
        try {
          const syncRes = await menuAPI.syncCatalog({ tenantId: t.tenantId, dryRun: false, disableNotInCatalog: true })
          const data = syncRes?.data ?? syncRes
          totalInserted += (data as { inserted?: number }).inserted ?? 0
          totalUpdated += (data as { updated?: number }).updated ?? 0
        } catch {
          // skip failed tenant, continue with others
        }
      }
      const parts: string[] = []
      if (totalInserted > 0) parts.push(`${totalInserted} menü oluşturuldu`)
      if (totalUpdated > 0) parts.push(`${totalUpdated} menü güncellendi`)
      if (parts.length) {
        toast({ title: 'Tüm Tenantlar', description: parts.join('. '), variant: 'success' })
      } else if (tenants.length > 0) {
        toast({ title: 'Bilgi', description: 'Tüm tenantların menü kataloğu güncel.', variant: 'info' })
      }
      setShowMenuCreateModal(false)
    } catch (err: unknown) {
      setMenuCreateError(err instanceof Error ? err.message : 'Oluşturulamadı')
    } finally {
      setSyncingAllMenus(false)
    }
  }, [toast])

  const refetchTenantUsers = useCallback(async () => {
    if (!selectedTenantForUsers) return
    const res = await usersApi.list({ tenantId: selectedTenantForUsers.tenantId, pageSize: 500 })
    setTenantUsers(res.items)
  }, [selectedTenantForUsers])

  const refetchTenantOrganizations = useCallback(async () => {
    if (!selectedTenantForWorkplaces) return
    const res = await organizationsApi.list({ tenantId: selectedTenantForWorkplaces.tenantId, pageSize: 500 })
    setTenantOrganizations(res.items)
  }, [selectedTenantForWorkplaces])

  const handleAddUser = useCallback(async () => {
    if (!selectedTenantForUsers) return
    const { email, firstName, lastName, phone, password } = newUserData
    if (!email.trim()) {
      toast({ title: 'Hata', description: 'E-posta zorunludur.', variant: 'danger' })
      return
    }
    if (password.length < 8) {
      toast({ title: 'Hata', description: 'Şifre en az 8 karakter olmalıdır.', variant: 'danger' })
      return
    }
    const userName = (firstName.trim() && lastName.trim())
      ? `${firstName.trim()}.${lastName.trim()}`.replace(/\s+/g, '').toLowerCase()
      : email.trim().split('@')[0]?.replace(/\s+/g, '').toLowerCase() ?? email.trim()
    setAddUserSubmitting(true)
    try {
      await usersApi.create({
        userName,
        email: email.trim() || null,
        phone: phone.trim() || null,
        password: password || null,
        tenantId: selectedTenantForUsers.tenantId,
      })
      toast({ title: 'Başarılı', description: 'Kullanıcı eklendi.', variant: 'success' })
      setNewUserData({ email: '', firstName: '', lastName: '', phone: '', password: '', forcePasswordChange: true })
      setShowAddUserModal(false)
      await refetchTenantUsers()
    } catch (err: unknown) {
      toast({ title: 'Hata', description: err instanceof Error ? err.message : 'Kullanıcı eklenemedi.', variant: 'danger' })
    } finally {
      setAddUserSubmitting(false)
    }
  }, [selectedTenantForUsers, newUserData, refetchTenantUsers, toast])

  const handleOpenEditUser = useCallback((u: UserItem) => {
    setEditingUser(u)
    setEditUserData({
      email: u.email ?? '',
      phone: u.phone ?? '',
      isActive: u.isActive,
    })
    setShowEditUserModal(true)
  }, [])

  const handleEditUser = useCallback(async () => {
    if (!editingUser) return
    setEditUserSubmitting(true)
    try {
      await usersApi.update(editingUser.userId, {
        email: editUserData.email.trim() || null,
        phone: editUserData.phone.trim() || null,
        isActive: editUserData.isActive,
      })
      toast({ title: 'Başarılı', description: 'Kullanıcı güncellendi.', variant: 'success' })
      setShowEditUserModal(false)
      setEditingUser(null)
      await refetchTenantUsers()
    } catch (err: unknown) {
      toast({ title: 'Hata', description: err instanceof Error ? err.message : 'Güncellenemedi.', variant: 'danger' })
    } finally {
      setEditUserSubmitting(false)
    }
  }, [editingUser, editUserData, refetchTenantUsers, toast])

  const handleAddRole = useCallback(async () => {
    if (!newRoleData.name.trim() || !newRoleData.code.trim()) {
      toast({ title: 'Hata', description: 'Rol adı ve kod zorunludur.', variant: 'danger' })
      return
    }
    setAddRoleSubmitting(true)
    try {
      toast({ title: 'Bilgi', description: 'Rol API henüz kullanıma açılmadı. Yakında eklenecek.', variant: 'info' })
      setNewRoleData({ name: '', code: '', description: '' })
      setShowAddRoleModal(false)
    } finally {
      setAddRoleSubmitting(false)
    }
  }, [newRoleData, toast])

  const handleAddWorkplace = useCallback(async () => {
    if (!selectedTenantForWorkplaces) return
    if (!newWorkplaceData.code.trim() || !newWorkplaceData.name.trim()) {
      toast({ title: 'Hata', description: 'Kod ve ad zorunludur.', variant: 'danger' })
      return
    }
    setAddWorkplaceSubmitting(true)
    try {
      await organizationsApi.create({
        tenantId: selectedTenantForWorkplaces.tenantId,
        code: newWorkplaceData.code.trim(),
        name: newWorkplaceData.name.trim(),
        description: newWorkplaceData.description.trim() || null,
        isActive: newWorkplaceData.isActive,
      })
      toast({ title: 'Başarılı', description: 'Organizasyon birimi eklendi.', variant: 'success' })
      setNewWorkplaceData({ code: '', name: '', description: '', isActive: true })
      setShowAddWorkplaceModal(false)
      await refetchTenantOrganizations()
    } catch (err: unknown) {
      toast({ title: 'Hata', description: err instanceof Error ? err.message : 'Birim eklenemedi.', variant: 'danger' })
    } finally {
      setAddWorkplaceSubmitting(false)
    }
  }, [selectedTenantForWorkplaces, newWorkplaceData, refetchTenantOrganizations, toast])

  const handleOpenEditWorkplace = useCallback((org: Organization) => {
    setEditingWorkplace(org)
    setEditWorkplaceData({
      code: org.code ?? '',
      name: org.name,
      description: org.description ?? '',
      isActive: org.isActive,
    })
    setShowEditWorkplaceModal(true)
  }, [])

  const handleEditWorkplace = useCallback(async () => {
    if (!editingWorkplace || !selectedTenantForWorkplaces) return
    if (!editWorkplaceData.code.trim() || !editWorkplaceData.name.trim()) {
      toast({ title: 'Hata', description: 'Kod ve ad zorunludur.', variant: 'danger' })
      return
    }
    setEditWorkplaceSubmitting(true)
    try {
      await organizationsApi.update({
        id: editingWorkplace.organizationId,
        tenantId: selectedTenantForWorkplaces.tenantId,
        code: editWorkplaceData.code.trim(),
        name: editWorkplaceData.name.trim(),
        description: editWorkplaceData.description.trim() || null,
        isActive: editWorkplaceData.isActive,
      })
      toast({ title: 'Başarılı', description: 'Birim güncellendi.', variant: 'success' })
      setShowEditWorkplaceModal(false)
      setEditingWorkplace(null)
      await refetchTenantOrganizations()
    } catch (err: unknown) {
      toast({ title: 'Hata', description: err instanceof Error ? err.message : 'Güncellenemedi.', variant: 'danger' })
    } finally {
      setEditWorkplaceSubmitting(false)
    }
  }, [editingWorkplace, selectedTenantForWorkplaces, editWorkplaceData, refetchTenantOrganizations, toast])

  const handleDeleteWorkplace = useCallback(async () => {
    if (!deleteWorkplaceTarget) return
    setDeletingWorkplace(true)
    try {
      await organizationsApi.delete(deleteWorkplaceTarget.organizationId)
      toast({ title: 'Başarılı', description: 'Birim silindi.', variant: 'success' })
      setConfirmDeleteWorkplaceOpen(false)
      setDeleteWorkplaceTarget(null)
      await refetchTenantOrganizations()
    } catch (err: unknown) {
      toast({ title: 'Hata', description: err instanceof Error ? err.message : 'Silinemedi.', variant: 'danger' })
    } finally {
      setDeletingWorkplace(false)
    }
  }, [deleteWorkplaceTarget, refetchTenantOrganizations, toast])

  return (
    <>
    <CrudPage<Tenant, TenantForm, any, any>
      title="Şirketler"
      subtitle="Tenant/şirket bilgilerini yönet"
      primaryActionLabel="Yeni Şirket"
      list={async (p) =>
        tenantsApi.list({
          search: p.search,
          page: p.page,
          pageSize: p.pageSize,
          sortBy: p.sortBy,
          sortDir: p.sortDir,
          status: p.filters.status === 'all' ? null : (p.filters.status as any),
        })
      }
      getRowId={(t) => t.tenantId}
      columns={[
        { key: 'name', header: 'Ad', sortable: true, render: (t) => <span className="font-medium">{t.name}</span> },
        { key: 'legalName', header: 'Ticari Ünvan', sortable: false, render: (t) => t.legalName ?? '-' },
        { key: 'taxNumber', header: 'VKN/TCKN', sortable: false, render: (t) => t.taxNumber ?? '-' },
        {
          key: 'status',
          header: 'Durum',
          sortable: true,
          widthClassName: 'w-[140px]',
          render: (t) =>
            t.status === 'active' ? (
              <Badge variant="success">Aktif</Badge>
            ) : t.status === 'inactive' ? (
              <Badge variant="warning">Pasif</Badge>
            ) : (
              <Badge variant="danger">Askıda</Badge>
            ),
        },
        {
          key: 'tenantActions',
          header: 'Hızlı Erişim',
          sortable: false,
          widthClassName: 'min-w-[200px]',
          render: (t) => (
            <TenantRowActions
              tenant={t}
              onUsers={handleOpenUsers}
              onRoles={handleOpenRoles}
              onWorkplaces={handleOpenWorkplaces}
              onMenus={handleOpenMenus}
            />
          ),
        },
      ]}
      searchPlaceholder="Ara (ad, ticari ünvan, vergi no)…"
      filters={[
        {
          id: 'status',
          label: 'Durum',
          value: 'all',
          options: [
            { label: 'Tümü', value: 'all' },
            { label: 'Aktif', value: 'active' },
            { label: 'Pasif', value: 'inactive' },
            { label: 'Askıda', value: 'suspended' },
          ],
        },
      ]}
      initialPageSize={10}
      modalTitles={{ create: 'Yeni Şirket', edit: 'Şirketi Düzenle' }}
      getCreateDefaultForm={() => ({
        name: '',
        legalName: '',
        industry: '',
        taxNumber: '',
        taxOffice: '',
        organizationType: '',
        foundationDate: '',
        address: '',
        country: '',
        city: '',
        authorizedPersonName: '',
        phone: '',
        email: '',
        website: '',
        description: '',

        timezone: 'Europe/Istanbul',
        language: 'tr',
        logoUrl: '',
        logoFile: null,

        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
        adminPassword: '',
        adminPasswordConfirm: '',
      })}
      getEditForm={(t) => ({
        name: t.name ?? '',
        legalName: (t as any).legalName ?? '',
        industry: (t as any).industry ?? '',
        taxNumber: (t as any).taxNumber ?? '',
        taxOffice: (t as any).taxOffice ?? '',
        organizationType: (t as any).organizationType ?? '',
        foundationDate: (t as any).foundationDate ?? '',
        address: (t as any).address ?? '',
        country: (t as any).country ?? '',
        city: (t as any).city ?? '',
        authorizedPersonName: (t as any).authorizedPersonName ?? '',
        phone: (t as any).phone ?? '',
        email: (t as any).email ?? '',
        website: (t as any).website ?? '',
        description: (t as any).description ?? '',

        // If read model includes settings (optional), map them; otherwise fall back to defaults.
        timezone: (t as any).timezone ?? 'Europe/Istanbul',
        language: (t as any).language ?? 'tr',
        logoUrl: (t as any).logoUrl ?? '',
        logoFile: null,

        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
        adminPassword: '',
        adminPasswordConfirm: '',
      })}
      // CrudPage requires a validate prop; validateWithMode is used for real validation.
      validate={() => ({})}
      validateWithMode={({ mode, form: f }) => ({
        name: !f.name.trim() ? 'Zorunlu alan' : undefined,
        legalName: !f.legalName.trim() ? 'Zorunlu alan' : undefined,
        industry: !f.industry.trim() ? 'Zorunlu alan' : undefined,
        organizationType: !f.organizationType.trim() ? 'Zorunlu alan' : undefined,
        authorizedPersonName: !f.authorizedPersonName.trim() ? 'Zorunlu alan' : undefined,
        timezone: !f.timezone.trim() ? 'Zorunlu alan' : undefined,
        email: f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) ? 'Geçerli bir e-posta girin' : undefined,
        adminFirstName: mode === 'create' && !f.adminFirstName.trim() ? 'Zorunlu alan' : undefined,
        adminLastName: mode === 'create' && !f.adminLastName.trim() ? 'Zorunlu alan' : undefined,
        adminEmail:
          mode === 'create' && !f.adminEmail.trim()
            ? 'Zorunlu alan'
            : f.adminEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.adminEmail.trim())
              ? 'Geçerli bir e-posta girin'
              : undefined,
        adminPassword: mode === 'create' && !f.adminPassword ? 'Zorunlu alan' : undefined,
        adminPasswordConfirm:
          mode === 'create' && !f.adminPasswordConfirm
            ? 'Zorunlu alan'
            : mode === 'create' && f.adminPasswordConfirm !== f.adminPassword
              ? 'Şifreler eşleşmiyor'
              : undefined,
        logoFile: f.logoFile && f.logoFile.size > 500 * 1024 ? 'Max 500KB' : undefined,
      })}
      renderForm={({ mode, form, setForm, touched, setTouched, errors, isSubmitting }) => (
        <TenantModalForm
          mode={mode}
          form={form}
          setForm={setForm}
          touched={touched}
          setTouched={setTouched}
          errors={errors}
          isSubmitting={isSubmitting}
          refDataGroups={refDataGroups}
        />
      )}
      buildCreatePayload={(f) => {
        const firstName = f.adminFirstName.trim()
        const lastName = f.adminLastName.trim()
        const email = f.adminEmail.trim()
        const userName =
          (firstName && lastName ? `${firstName}.${lastName}` : email.split('@')[0] ?? '')
            .replace(/\s+/g, '')
            .toLowerCase()

        return {
          tenant: {
            name: f.name.trim(),
            status: 'active',
            legalName: emptyToNull(f.legalName),
            industry: emptyToNull(f.industry),
            taxNumber: emptyToNull(f.taxNumber),
            taxOffice: emptyToNull(f.taxOffice),
            organizationType: emptyToNull(f.organizationType),
            foundationDate: foundationDateToApi(f.foundationDate),
            address: emptyToNull(f.address),
            country: emptyToNull(f.country),
            city: emptyToNull(f.city),
            authorizedPersonName: emptyToNull(f.authorizedPersonName),
            phone: emptyToNull(f.phone),
            email: emptyToNull(f.email),
            website: emptyToNull(f.website),
            description: emptyToNull(f.description),
          },
          settings: {
            logoUrl: emptyToNull(f.logoUrl),
            language: emptyToNull(f.language),
            timezone: emptyToNull(f.timezone),
          },
          // Backend ignores extra fields; keep UI mapping to Pearl labels.
          adminUser: {
            firstName,
            lastName,
            userName,
            email,
            password: f.adminPassword,
            forcePasswordChange: true,
          },
        }
      }}
      buildUpdatePayload={(f) => ({
        tenant: {
          name: f.name.trim(),
          legalName: emptyToNull(f.legalName),
          industry: emptyToNull(f.industry),
          taxNumber: emptyToNull(f.taxNumber),
          taxOffice: emptyToNull(f.taxOffice),
          organizationType: emptyToNull(f.organizationType),
          foundationDate: foundationDateToApi(f.foundationDate),
          address: emptyToNull(f.address),
          country: emptyToNull(f.country),
          city: emptyToNull(f.city),
          authorizedPersonName: emptyToNull(f.authorizedPersonName),
          phone: emptyToNull(f.phone),
          email: emptyToNull(f.email),
          website: emptyToNull(f.website),
          description: emptyToNull(f.description),
        },
        settings: {
          logoUrl: emptyToNull(f.logoUrl),
          language: emptyToNull(f.language),
          timezone: emptyToNull(f.timezone),
        },
      })}
      create={(payload) => tenantsApi.createWithAdmin(payload as any)}
      update={async (id, payload) => {
        const p = payload as any
        const tenantPayload = p?.tenant ?? {}
        const settingsPayload = p?.settings ?? {}
        await tenantsApi.update({ id: Number(id), ...(tenantPayload as any) })
        // keep settings in sync (Auth.TenantSettings)
        await tenantsApi.updateSettings(Number(id), settingsPayload)
      }}
      remove={(id) => tenantsApi.delete(Number(id))}
      getCreatedId={(res) => res?.data?.tenantId}
      pollTimeoutMs={5000}
      pollIntervalMs={500}
    />

      {/* Modal: Kullanıcılar (Pearl-style, işlevsel) */}
      <Dialog open={showUsersModal} onOpenChange={setShowUsersModal}>
        <DialogContent size="2xl" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between space-y-0">
            <DialogTitle>{selectedTenantForUsers?.name ?? 'Şirket'} — Kullanıcılar</DialogTitle>
            <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => { setNewUserData({ email: '', firstName: '', lastName: '', phone: '', password: '', forcePasswordChange: true }); setShowAddUserModal(true) }}>
              Ekle
            </Button>
          </DialogHeader>
          {usersLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Yükleniyor…</p>
          ) : tenantUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kullanıcı adı</TableHead>
                    <TableHead>E-posta</TableHead>
                    <TableHead>Telefon</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead className="w-[100px]">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenantUsers.map((u) => (
                    <TableRow key={u.userId}>
                      <TableCell className="font-medium">{u.userName}</TableCell>
                      <TableCell>{u.email ?? '—'}</TableCell>
                      <TableCell>{u.phone ?? '—'}</TableCell>
                      <TableCell>
                        {u.isActive ? <Badge variant="success">Aktif</Badge> : <Badge variant="warning">Pasif</Badge>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" aria-label="Düzenle" onClick={() => handleOpenEditUser(u)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" aria-label="Sil" onClick={() => toast({ title: 'Bilgi', description: 'Kullanıcı silme özelliği yakında eklenecek.', variant: 'info' })}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Bu şirkette kullanıcı yok. &quot;Ekle&quot; ile yeni kullanıcı ekleyebilirsiniz.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUsersModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alt modal: Yeni Kullanıcı */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Yeni Kullanıcı Ekle</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <Input label="E-posta *" type="email" value={newUserData.email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewUserData((d) => ({ ...d, email: e.target.value }))} placeholder="ornek@email.com" disabled={addUserSubmitting} />
            <Input label="Ad" value={newUserData.firstName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewUserData((d) => ({ ...d, firstName: e.target.value }))} placeholder="Ad" disabled={addUserSubmitting} />
            <Input label="Soyadı" value={newUserData.lastName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewUserData((d) => ({ ...d, lastName: e.target.value }))} placeholder="Soyadı" disabled={addUserSubmitting} />
            <Input label="Telefon" value={newUserData.phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewUserData((d) => ({ ...d, phone: e.target.value }))} placeholder="+90 (5XX) XXX XX XX" disabled={addUserSubmitting} />
            <Input label="Şifre *" type="password" value={newUserData.password} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewUserData((d) => ({ ...d, password: e.target.value }))} placeholder="En az 8 karakter" disabled={addUserSubmitting} helperText="En az 8 karakter olmalıdır" />
            <div className="flex items-center gap-2">
              <Switch id="force-pw" checked={newUserData.forcePasswordChange} onCheckedChange={(v: boolean) => setNewUserData((d) => ({ ...d, forcePasswordChange: v }))} disabled={addUserSubmitting} />
              <Label htmlFor="force-pw">İlk girişte şifre değiştirme zorunlu</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserModal(false)} disabled={addUserSubmitting}>İptal</Button>
            <Button onClick={handleAddUser} loading={addUserSubmitting} disabled={addUserSubmitting}>Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alt modal: Kullanıcı Düzenle */}
      <Dialog open={showEditUserModal} onOpenChange={(open) => { if (!open) { setShowEditUserModal(false); setEditingUser(null) } }}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Kullanıcı Düzenle {editingUser ? `— ${editingUser.userName}` : ''}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <Input label="E-posta" type="email" value={editUserData.email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditUserData((d) => ({ ...d, email: e.target.value }))} placeholder="ornek@email.com" disabled={editUserSubmitting} />
            <Input label="Telefon" value={editUserData.phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditUserData((d) => ({ ...d, phone: e.target.value }))} placeholder="+90 (5XX) XXX XX XX" disabled={editUserSubmitting} />
            <div className="flex items-center gap-2">
              <Switch id="edit-user-active" checked={editUserData.isActive} onCheckedChange={(v: boolean) => setEditUserData((d) => ({ ...d, isActive: v }))} disabled={editUserSubmitting} />
              <Label htmlFor="edit-user-active">Aktif</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditUserModal(false); setEditingUser(null) }} disabled={editUserSubmitting}>İptal</Button>
            <Button onClick={handleEditUser} loading={editUserSubmitting} disabled={editUserSubmitting}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Roller (Pearl-style, işlevsel) */}
      <Dialog open={showRolesModal} onOpenChange={setShowRolesModal}>
        <DialogContent size="2xl" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between space-y-0">
            <DialogTitle>{selectedTenantForRoles?.name ?? 'Şirket'} — Roller</DialogTitle>
            <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => { setNewRoleData({ name: '', code: '', description: '' }); setShowAddRoleModal(true) }}>
              Ekle
            </Button>
          </DialogHeader>
          {rolesLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Yükleniyor…</p>
          ) : tenantRoles.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rol adı</TableHead>
                    <TableHead>Kod</TableHead>
                    <TableHead>Açıklama</TableHead>
                    <TableHead className="w-[100px]">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenantRoles.map((r) => (
                    <TableRow key={r.roleId}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>{r.code ?? '—'}</TableCell>
                      <TableCell>{r.description ?? '—'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" aria-label="Düzenle" onClick={() => toast({ title: 'Bilgi', description: 'Rol düzenleme yakında eklenecek.', variant: 'info' })}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" aria-label="Sil" onClick={() => toast({ title: 'Bilgi', description: 'Rol silme özelliği yakında eklenecek.', variant: 'info' })}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Bu şirkette rol tanımı yok. Rol ekleme özelliği yakında eklenecek.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRolesModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alt modal: Yeni Rol */}
      <Dialog open={showAddRoleModal} onOpenChange={setShowAddRoleModal}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Yeni Rol Ekle</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <Input label="Rol adı *" value={newRoleData.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewRoleData((d) => ({ ...d, name: e.target.value }))} placeholder="Örn: Yönetici" disabled={addRoleSubmitting} />
            <Input label="Kod *" value={newRoleData.code} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewRoleData((d) => ({ ...d, code: e.target.value }))} placeholder="ADMIN" disabled={addRoleSubmitting} />
            <Input label="Açıklama" value={newRoleData.description} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewRoleData((d) => ({ ...d, description: e.target.value }))} placeholder="Opsiyonel" disabled={addRoleSubmitting} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRoleModal(false)} disabled={addRoleSubmitting}>İptal</Button>
            <Button onClick={handleAddRole} loading={addRoleSubmitting} disabled={addRoleSubmitting}>Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: İşyerleri / Organizasyon Birimleri (Pearl-style, işlevsel) */}
      <Dialog open={showWorkplacesModal} onOpenChange={setShowWorkplacesModal}>
        <DialogContent size="2xl" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between space-y-0">
            <DialogTitle>{selectedTenantForWorkplaces?.name ?? 'Şirket'} — İşyerleri</DialogTitle>
            <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => { setNewWorkplaceData({ code: '', name: '', description: '', isActive: true }); setShowAddWorkplaceModal(true) }}>
              Ekle
            </Button>
          </DialogHeader>
          {workplacesLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Yükleniyor…</p>
          ) : tenantOrganizations.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kod</TableHead>
                    <TableHead>Ad</TableHead>
                    <TableHead>Açıklama</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead className="w-[100px]">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenantOrganizations.map((o) => (
                    <TableRow key={o.organizationId}>
                      <TableCell className="font-medium">{o.code ?? '—'}</TableCell>
                      <TableCell>{o.name}</TableCell>
                      <TableCell>{o.description ?? '—'}</TableCell>
                      <TableCell>
                        {o.isActive ? <Badge variant="success">Aktif</Badge> : <Badge variant="warning">Pasif</Badge>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" aria-label="Düzenle" onClick={() => handleOpenEditWorkplace(o)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" aria-label="Sil" onClick={() => { setDeleteWorkplaceTarget(o); setConfirmDeleteWorkplaceOpen(true) }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Bu şirkette işyeri / organizasyon birimi bulunmuyor. &quot;Ekle&quot; ile yeni birim ekleyebilirsiniz.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkplacesModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alt modal: Yeni Birim (İşyeri) */}
      <Dialog open={showAddWorkplaceModal} onOpenChange={setShowAddWorkplaceModal}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Yeni Birim Ekle</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <Input label="Kod *" value={newWorkplaceData.code} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewWorkplaceData((d) => ({ ...d, code: e.target.value }))} placeholder="Örn: MDR" disabled={addWorkplaceSubmitting} />
            <Input label="Ad *" value={newWorkplaceData.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewWorkplaceData((d) => ({ ...d, name: e.target.value }))} placeholder="Merkez" disabled={addWorkplaceSubmitting} />
            <Input label="Açıklama" value={newWorkplaceData.description} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewWorkplaceData((d) => ({ ...d, description: e.target.value }))} placeholder="Opsiyonel" disabled={addWorkplaceSubmitting} />
            <div className="flex items-center gap-2">
              <Switch id="workplace-active" checked={newWorkplaceData.isActive} onCheckedChange={(v: boolean) => setNewWorkplaceData((d) => ({ ...d, isActive: v }))} disabled={addWorkplaceSubmitting} />
              <Label htmlFor="workplace-active">Aktif</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddWorkplaceModal(false)} disabled={addWorkplaceSubmitting}>İptal</Button>
            <Button onClick={handleAddWorkplace} loading={addWorkplaceSubmitting} disabled={addWorkplaceSubmitting}>Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alt modal: Birim Düzenle */}
      <Dialog open={showEditWorkplaceModal} onOpenChange={(open) => { if (!open) { setShowEditWorkplaceModal(false); setEditingWorkplace(null) } }}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Birim Düzenle</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <Input label="Kod *" value={editWorkplaceData.code} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditWorkplaceData((d) => ({ ...d, code: e.target.value }))} placeholder="Örn: MDR" disabled={editWorkplaceSubmitting} />
            <Input label="Ad *" value={editWorkplaceData.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditWorkplaceData((d) => ({ ...d, name: e.target.value }))} placeholder="Merkez" disabled={editWorkplaceSubmitting} />
            <Input label="Açıklama" value={editWorkplaceData.description} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditWorkplaceData((d) => ({ ...d, description: e.target.value }))} placeholder="Opsiyonel" disabled={editWorkplaceSubmitting} />
            <div className="flex items-center gap-2">
              <Switch id="edit-workplace-active" checked={editWorkplaceData.isActive} onCheckedChange={(v: boolean) => setEditWorkplaceData((d) => ({ ...d, isActive: v }))} disabled={editWorkplaceSubmitting} />
              <Label htmlFor="edit-workplace-active">Aktif</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditWorkplaceModal(false); setEditingWorkplace(null) }} disabled={editWorkplaceSubmitting}>İptal</Button>
            <Button onClick={handleEditWorkplace} loading={editWorkplaceSubmitting} disabled={editWorkplaceSubmitting}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onay: İşyeri sil */}
      <ConfirmDialog
        open={confirmDeleteWorkplaceOpen}
        onOpenChange={setConfirmDeleteWorkplaceOpen}
        title="İşyeri sil"
        description={deleteWorkplaceTarget ? `"${deleteWorkplaceTarget.name}" birimini silmek istediğinize emin misiniz?` : ''}
        confirmLabel="Sil"
        onConfirm={handleDeleteWorkplace}
        loading={deletingWorkplace}
      />

      {/* Modal: Menüleri Oluştur */}
      <Dialog open={showMenuCreateModal} onOpenChange={setShowMenuCreateModal}>
        <DialogContent size="2xl" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{targetTenantForMenus?.name ?? 'Şirket'} — Menüleri Oluştur</DialogTitle>
          </DialogHeader>
          {menuCreateError && (
            <p className="text-sm text-amber-600 dark:text-amber-400">{menuCreateError}</p>
          )}
          {menusToCreate.length > 0 && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {menusToCreate.length} eksik menü bulundu. Aşağıdaki menüler oluşturulacak.
              </p>
              <div className="max-h-[40vh] overflow-y-auto space-y-2 my-4">
                {menusToCreate.map((menu, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{menu.label}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{menu.route}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMenuCreateModal(false)} disabled={creatingMenus || syncingAllMenus}>İptal</Button>
            <Button variant="outline" onClick={handleSyncAllTenantsMenus} loading={syncingAllMenus} disabled={creatingMenus || syncingAllMenus}>
              {syncingAllMenus ? 'Tüm tenantlar senkronize ediliyor…' : 'Tüm Tenantlar İçin Senkronize Et'}
            </Button>
            <Button onClick={handleCreateMenus} loading={creatingMenus} disabled={creatingMenus || syncingAllMenus}>
              {creatingMenus ? 'Senkronize ediliyor…' : `${targetTenantForMenus?.name ?? 'Bu tenant'} için Senkronize Et`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function TenantModalForm(props: {
  mode: 'create' | 'edit'
  form: TenantForm
  setForm: React.Dispatch<React.SetStateAction<TenantForm>>
  touched: boolean
  setTouched: (t: boolean) => void
  errors: Record<string, string | undefined>
  isSubmitting: boolean
  refDataGroups: Record<string, { items: RefDataItem[]; loading: boolean; error: string | null }>
}) {
  const { mode, form, setForm, touched, setTouched, errors, isSubmitting, refDataGroups } = props
  const lookupsGroups = useLookups(form.country || null)
  const refGroups = React.useMemo(
    () => ({ ...refDataGroups, ...lookupsGroups }),
    [refDataGroups, lookupsGroups]
  )
  const formId = React.useId()
  const [search, setSearch] = React.useState<Record<RefDataSelectId, string>>(() =>
    REF_DATA_SELECT_IDS.reduce((acc, k) => ({ ...acc, [k]: '' }), {} as Record<RefDataSelectId, string>)
  )

  const normalizeRefValue = React.useCallback((current: string, options: RefDataItem[]) => {
    const v = (current ?? '').trim()
    if (!v) return ''
    // Preferred: stored as Code (future-proof)
    if (options.some((o) => o.code === v)) return v
    // Backward compatible: if old records stored DisplayName, map to Code
    const matchByLabel = options.find((o) => (o.displayName ?? '').trim().toLowerCase() === v.toLowerCase())
    return matchByLabel?.code ?? v
  }, [])

  // When refdata arrives, normalize existing values so Select can display them (edit-mode compatibility).
  const didNormalize = React.useRef(false)
  React.useEffect(() => {
    didNormalize.current = false
  }, [mode])

  React.useEffect(() => {
    if (refGroups.industry?.loading || refGroups.country?.loading || refGroups.city?.loading || refGroups.organizationType?.loading || refGroups.timezone?.loading || refGroups.language?.loading) return
    if (didNormalize.current) return
    didNormalize.current = true

    setForm((s) => ({
      ...s,
      industry: normalizeRefValue(s.industry, refGroups.industry?.items ?? []),
      organizationType: normalizeRefValue(s.organizationType, refGroups.organizationType?.items ?? []),
      country: normalizeRefValue(s.country, refGroups.country?.items ?? []),
      city: normalizeRefValue(s.city, refGroups.city?.items ?? []),
      timezone: normalizeRefValue(s.timezone, refGroups.timezone?.items ?? []),
      language: normalizeRefValue(s.language, refGroups.language?.items ?? []),
    }))
  }, [normalizeRefValue, refGroups, setForm])

  return (
    <div className="grid gap-[18px]">
      <Section title="Organizasyon Bilgileri">
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Organizasyon Adı *"
            placeholder="Organizasyon adını girin"
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, name: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            disabled={isSubmitting}
            leftIcon={<i className="material-symbols-outlined text-lg">business</i>}
          />
          <Input
            label="Ticari Unvan *"
            placeholder="Ticari unvanı girin"
            value={form.legalName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, legalName: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.legalName}
            helperText={touched ? errors.legalName : undefined}
            disabled={isSubmitting}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <RefDataSelect
            id="industry"
            formId={formId}
            search={search}
            setSearch={setSearch}
            refGroups={refGroups}
            touched={touched}
            errors={errors}
            isSubmitting={isSubmitting}
            label="Sektör / Faaliyet Alanı *"
            value={form.industry}
            onChange={(v) => setForm((s) => ({ ...s, industry: v }))}
            options={refGroups.industry?.items ?? []}
            placeholder="Sektörü girin"
          />

          <FormField
            label="Şirket Logosu"
            helperText="PNG, JPG, GIF (Max 500KB, otomatik olarak sıkıştırılır)"
            error={touched && !!errors.logoFile}
            errorMessage={touched ? errors.logoFile : undefined}
            id={`${formId}-logo`}
          >
            <input
              id={`${formId}-logo`}
              name="logo"
              type="file"
              accept="image/png,image/jpeg,image/gif"
              className="h-[55px] w-full rounded-md border border-gray-200 dark:border-[#172036] bg-white dark:bg-[#0c1427] px-[17px] text-sm outline-none transition-all focus:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSubmitting}
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null
                setTouched(true)
                if (!file) {
                  setForm((s) => ({ ...s, logoFile: null, logoUrl: '' }))
                  return
                }
                if (file.size > 500 * 1024) {
                  setForm((s) => ({ ...s, logoFile: file }))
                  return
                }
                const reader = new FileReader()
                reader.onload = () => {
                  const url = typeof reader.result === 'string' ? reader.result : ''
                  setForm((s) => ({ ...s, logoFile: file, logoUrl: url }))
                }
                reader.readAsDataURL(file)
              }}
            />
            {form.logoUrl ? (
              <div className="mt-2">
                <img src={form.logoUrl} alt="logo preview" className="h-12 w-12 rounded-md border trezo-border-color object-cover" />
              </div>
            ) : null}
          </FormField>

          <Input
            label="Vergi Numarası"
            placeholder="Vergi numarası"
            value={form.taxNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, taxNumber: e.target.value }))}
            disabled={isSubmitting}
          />

          <Input
            label="Vergi Dairesi"
            placeholder="Vergi dairesi"
            value={form.taxOffice}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, taxOffice: e.target.value }))}
            disabled={isSubmitting}
          />

          <RefDataSelect
            id="organizationType"
            formId={formId}
            search={search}
            setSearch={setSearch}
            refGroups={refGroups}
            touched={touched}
            errors={errors}
            isSubmitting={isSubmitting}
            label="Kuruluş Tipi *"
            value={form.organizationType}
            onChange={(v) => setForm((s) => ({ ...s, organizationType: v }))}
            options={refGroups.organizationType?.items ?? []}
            placeholder="Seçiniz"
          />

          <Input
            label="Kuruluş Tarihi"
            placeholder="mm/dd/yyyy"
            value={form.foundationDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, foundationDate: formatMmDdYyyy(e.target.value) }))
            }
            disabled={isSubmitting}
          />

          <Textarea
            label="Resmi Adres"
            placeholder="Adres"
            value={form.address}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm((s) => ({ ...s, address: e.target.value }))}
            disabled={isSubmitting}
          />

          <RefDataSelect
            id="country"
            formId={formId}
            search={search}
            setSearch={setSearch}
            refGroups={refGroups}
            touched={touched}
            errors={errors}
            isSubmitting={isSubmitting}
            label="Ülke"
            value={form.country}
            onChange={(v) => {
              setForm((s) => ({ ...s, country: v, city: '' }))
            }}
            options={refGroups.country?.items ?? []}
            placeholder="Ülke"
          />

          <RefDataSelect
            id="city"
            formId={formId}
            search={search}
            setSearch={setSearch}
            refGroups={refGroups}
            touched={touched}
            errors={errors}
            isSubmitting={isSubmitting}
            label="Şehir"
            value={form.city}
            onChange={(v) => setForm((s) => ({ ...s, city: v }))}
            options={(() => {
              const cities = refGroups.city?.items ?? []
              const country = (form.country ?? '').trim()
              if (!country) return cities
              const byPrefix = cities.filter((c) => (c.code ?? '').toUpperCase().startsWith(`${country.toUpperCase()}_`) || (c.code ?? '').toUpperCase().startsWith(`${country.toUpperCase()}-`))
              return byPrefix.length ? byPrefix : cities
            })()}
            placeholder="Şehir"
          />
        </div>
      </Section>

      <Section
        title="Yönetici Bilgileri"
        description={mode === 'edit' ? undefined : undefined}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Ad *"
            placeholder="Ad"
            value={form.adminFirstName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, adminFirstName: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.adminFirstName}
            helperText={touched ? errors.adminFirstName : undefined}
            disabled={isSubmitting || mode === 'edit'}
          />
          <Input
            label="Soyadı *"
            placeholder="Soyadı"
            value={form.adminLastName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, adminLastName: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.adminLastName}
            helperText={touched ? errors.adminLastName : undefined}
            disabled={isSubmitting || mode === 'edit'}
          />
          <Input
            label="E-posta *"
            placeholder="pearl.admin@pearlhr.com"
            value={form.adminEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, adminEmail: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.adminEmail}
            helperText={touched ? errors.adminEmail : undefined}
            disabled={isSubmitting || mode === 'edit'}
          />
          <Input
            label="Şifre *"
            type="password"
            value={form.adminPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, adminPassword: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.adminPassword}
            helperText={touched ? errors.adminPassword : undefined}
            disabled={isSubmitting || mode === 'edit'}
          />
          <Input
            label="Şifre Tekrar *"
            type="password"
            value={form.adminPasswordConfirm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, adminPasswordConfirm: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.adminPasswordConfirm}
            helperText={touched ? errors.adminPasswordConfirm : undefined}
            disabled={isSubmitting || mode === 'edit'}
          />
        </div>
      </Section>

      <Section title="İletişim Bilgileri">
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Yetkili Kişi Adı *"
            placeholder="Yetkili kişi adı"
            value={form.authorizedPersonName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, authorizedPersonName: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.authorizedPersonName}
            helperText={touched ? errors.authorizedPersonName : undefined}
            disabled={isSubmitting}
          />
          <Input
            label="Telefon Numarası"
            placeholder="+90 (XXX) XXX-XXXX"
            value={form.phone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, phone: e.target.value }))}
            disabled={isSubmitting}
          />
          <Input
            label="E-posta Adresi"
            placeholder="info@example.com"
            value={form.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, email: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.email}
            helperText={touched ? errors.email : undefined}
            disabled={isSubmitting}
          />
          <Input
            label="Web Sitesi"
            placeholder="https://example.com"
            value={form.website}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, website: e.target.value }))}
            disabled={isSubmitting}
          />
        </div>
      </Section>

      <Section title="Ayarlar">
        <div className="grid gap-4 md:grid-cols-2">
          <RefDataSelect
            id="timezone"
            formId={formId}
            search={search}
            setSearch={setSearch}
            refGroups={refGroups}
            touched={touched}
            errors={errors}
            isSubmitting={isSubmitting}
            label="Zaman Dilimi *"
            value={form.timezone}
            onChange={(v) => setForm((s) => ({ ...s, timezone: v }))}
            options={refGroups.timezone?.items ?? []}
            placeholder="Europe/Istanbul (UTC+3)"
          />
          <RefDataSelect
            id="language"
            formId={formId}
            search={search}
            setSearch={setSearch}
            refGroups={refGroups}
            touched={touched}
            errors={errors}
            isSubmitting={isSubmitting}
            label="Dil"
            value={form.language}
            onChange={(v) => setForm((s) => ({ ...s, language: v }))}
            options={refGroups.language?.items ?? []}
            placeholder="Türkçe"
          />
        </div>
      </Section>

      <Section title="Açıklama">
        <Textarea
          label="Açıklama"
          value={form.description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm((s) => ({ ...s, description: e.target.value }))}
          disabled={isSubmitting}
        />
      </Section>
    </div>
  )
}

